import { toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useAiJobByJobId } from '@/hooks/useAiJobByJobId';
import type { AiJobState } from '@/lib/aiJobSocket';

interface DispatchResponse {
  success: boolean;
  data?: AiJobState;
  error?: string;
}

interface UseAiJobMutationOptions {
  /** Endpoint to POST. Required. */
  endpoint: string;
  /** Friendly label for error toasts (e.g. "Failed to generate descriptions"). */
  errorLabel?: string;
}

interface UseAiJobMutationResult<TBody> {
  /** Kick off the job. The returned jobId can be passed to background handoff. */
  start: (body: TBody) => Promise<string | null>;
  /** Latest state for the active job, if any. */
  state: AiJobState | null;
  /** True until the controller responds with 202. */
  isStarting: boolean;
  /** True while the job is running on the server. */
  isRunning: boolean;
  /** Set after a 'completed' or 'failed' status is received. */
  isFinished: boolean;
  /**
   * True when a dispatched job hasn't reached a terminal status within the
   * expected window — i.e. no completion ever came back. Distinguishes "still
   * working" from "silently stuck" (e.g. the Inngest function never ran) so the
   * UI can surface an error and let the user retry instead of hanging forever.
   */
  isStalled: boolean;
  /** Resets the local jobId so the dialog returns to its idle state. */
  reset: () => void;
  /** The most recent jobId, or null if no job has been started. */
  jobId: string | null;
}

/**
 * How long to wait for a dispatched job to reach a terminal status before
 * treating it as stalled. On-demand jobs normally finish in 30–60s; this gives
 * generous headroom, and a genuinely-slow job that finishes later still closes
 * the dialog (the completion handler runs regardless). Retrying while a real
 * job is mid-flight is safe — the dispatcher dedupes to the same job.
 */
const STALL_TIMEOUT_MS = 90_000;

/**
 * Hook that wraps the dispatcher → socket-subscribe → terminal-status flow
 * for an on-demand AI job. The dialog provides the endpoint URL and any body
 * fields; the hook returns the job state and lifecycle booleans.
 *
 * Usage:
 * ```tsx
 * const job = useAiJobMutation({ endpoint: `/attractions/${id}/generate-descriptions` });
 * await job.start({ descriptionType: 'both' });
 * job.isRunning, job.state?.status === 'completed', etc.
 * ```
 */
export function useAiJobMutation<TBody = Record<string, unknown>>(
  options: UseAiJobMutationOptions,
): UseAiJobMutationResult<TBody> {
  const apiClient = useApiClient();
  const { endpoint, errorLabel = 'Generation failed to start' } = options;
  const [jobId, setJobId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (body: TBody): Promise<DispatchResponse> => {
      return apiClient.post<DispatchResponse>(endpoint, body ?? {});
    },
    onError: (err: Error) => {
      toast.error(err?.message || errorLabel);
    },
  });

  const start = useCallback(
    async (body: TBody): Promise<string | null> => {
      try {
        const response = await mutation.mutateAsync(body);
        const newJobId = response.data?.jobId ?? null;
        if (newJobId) setJobId(newJobId);
        return newJobId;
      } catch {
        return null;
      }
    },
    [mutation],
  );

  const reset = useCallback(() => setJobId(null), []);

  const { state } = useAiJobByJobId(jobId);
  const isRunning = state?.status === 'running';
  const isFinished = state?.status === 'completed' || state?.status === 'failed';

  // Surface a stall when a dispatched job never reaches a terminal status.
  // Arm the timer once we have a jobId; clear it when the job finishes or is
  // reset. Intermediate updates (null → running) don't reset the deadline.
  const [isStalled, setIsStalled] = useState(false);
  useEffect(() => {
    setIsStalled(false);
    if (!jobId || isFinished) return;
    const timer = setTimeout(() => setIsStalled(true), STALL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [jobId, isFinished]);

  return {
    start,
    state,
    isStarting: mutation.isPending,
    isRunning,
    isFinished,
    isStalled,
    reset,
    jobId,
  };
}
